import React from 'react';
import { Link } from 'react-router-dom';
import { Hash, Info } from 'lucide-react';
import { PageHeader, Card, Table, useDialect, originalElement } from './common';

/** The elements the switch uses, and what a participant should know about each — the format comes from the profile. */
const ELEMENT_NOTES = {
  2: 'test ranges only; masked in every log and in the ledger',
  3: '000000 for a purchase',
  4: 'minor units; the cents choose the test scenario',
  7: 'UTC; with DE 11 it identifies a message',
  11: 'unique per transaction; echoed in every response',
  12: "the terminal's clock",
  13: "the terminal's clock",
  22: '',
  24: 'see network management, below',
  32: 'DE 32 at sign-on names you',
  33: 'the forwarding institution, when there is one',
  35: 'accepted in place of DE 2; never stored',
  37: '',
  38: 'set on approvals',
  39: 'see response codes, above',
  41: '',
  49: 'e.g. 646 (RWF)',
  70: 'see network management, below',
  90: 'identifies the original in a reversal — see Getting Started',
  95: 'actual amounts; all zero in a full reversal',
  128: 'when your link authenticates messages',
};
const ELEMENTS_USED = [2, 3, 4, 7, 11, 12, 13, 22, 24, 32, 33, 35, 37, 38, 39, 41, 49, 70, 90, 95, 128];

/**
 * The vocabulary of the card link: message types, response codes, network
 * management codes and the data elements this switch uses — all from the
 * profile in force, so a participant on a 1993 switch reads 1200, DE 24 and
 * 000 here, not the 1987 baseline. Only codes the switch actually sends are
 * listed: a participant's system maps codes to behaviour, so a code listed but
 * never sent would be a branch built for nothing.
 */
export default function CardCodes() {
  const d = useDialect();
  const { mti, nm, rc, code } = d;
  const fn = (key) => nm.codes?.[key];

  const responseRows = [
    ['approved', 'Approved', 'a test card with an ordinary amount; any sign-on, echo or acknowledgement that succeeded', '—'],
    ['doNotHonour', 'Do not honour', 'amount ending .05', 'decline at the terminal'],
    ['invalidCard', 'Invalid card', 'a card outside the test ranges', 'use a test card — see Test Cards'],
    ['formatError', 'Format error', 'a message that would not decode, or failed its MAC', 'the ledger shows the finding: field, byte and reason'],
    ['institutionUnknown', 'Institution not known', `a sign-on whose DE 32 is not enrolled for you on this corridor`, 'check your institution id with AfricaNenda'],
    ['functionNotSupported', 'Function not supported', `a key exchange, or a reversal request (${mti('0400')})`, `use a static key; reverse with ${mti('0420')}`],
    ['insufficientFunds', 'Insufficient funds', 'amount ending .51', 'decline at the terminal'],
    ['securityViolation', 'Security violation', "a sign-on claiming another participant's institution", 'sign on with your own id'],
    ['switchInoperative', 'Issuer or switch inoperative', 'a transaction on a link whose keys are not synchronised', 'complete the key exchange first'],
    ['duplicateTransaction', 'Duplicate transaction', `a new request reusing the ${d.defines(7) ? 'STAN and DE 7' : 'STAN'} of one already answered`,
      `give every new transaction a new STAN; retransmit as a repeat (${mti('0201')})`],
  ].filter(([key]) => rc.codes?.[key] != null).map(([key, ...rest]) => [code(key), ...rest]);

  const nmRows = [
    ['signOn', 'Sign-on', 'identifies the link by DE 32; transactions accepted afterwards'],
    ['signOff', 'Sign-off', 'new transactions ignored until the next sign-on; replies to anything in flight still honoured'],
    ['echoTest', 'Echo test', 'answered by the switch; the switch also sends one after 60 s of quiet'],
    ['pinKeyChange', 'PIN key change', `declined ${code('functionNotSupported')} — no real PIN is ever processed here`],
    ['macKeyChange', 'MAC key change', `declined ${code('functionNotSupported')} — key exchange over the wire is not built yet`],
    ['text', 'Text', 'acknowledged'],
  ].filter(([key]) => fn(key)).map(([key, ...rest]) => [fn(key), ...rest]);

  // The element that names a reversal's original is wherever this dialect puts it.
  const packed = d.originalData.layout !== 'fields' ? d.originalData.field : null;
  const notes = { ...ELEMENT_NOTES };
  if (packed) notes[packed] = 'identifies the original in a reversal — see Getting Started';
  if (packed !== 90) notes[90] = '';
  const elements = d.dialect?.fields
    ? d.dialect.fields.filter((f) => ELEMENTS_USED.includes(f.de) || f.de === nm.field || f.de === rc.field || f.de === packed)
        .map((f) => [String(f.de), f.name, f.format, notes[f.de] ?? ''])
    : null;

  return (
    <div className="space-y-6 max-w-5xl">
      <PageHeader icon={Hash} title="Codes & Messages">
        What travels on the card link, and what each answer means — as the profile in force spells it. This switch runs{' '}
        <span className="font-mono">{d.id}</span>; every message type, code and element below is that profile's.
      </PageHeader>

      <Card title="Message types (MTI)">
        <Table head={['Request', 'Response', 'Who sends it', 'Use']} mono={[0, 1]} rows={[
          [`${mti('0100')} / ${mti('0101')}`, mti('0110'), 'either side', `authorisation; ${mti('0101')} is its repeat`],
          [`${mti('0200')} / ${mti('0201')}`, mti('0210'), 'either side', `financial transaction (purchase); ${mti('0201')} is its repeat`],
          [`${mti('0120')} / ${mti('0220')}`, `${mti('0130')} / ${mti('0230')}`, 'either side', 'advice of a decision already made — acknowledged, not decided'],
          [`${mti('0420')} / ${mti('0421')}`, mti('0430'), 'either side', `reversal advice; always acknowledged, matched or not; ${mti('0421')} is its repeat`],
          [nm.requestMti, nm.responseMti, 'either side', `network management: sign-on, sign-off, echo, key exchange — the function in DE ${nm.field}`],
        ]} />
        <p className="text-xs text-gray-500 mt-3">
          A repeat (odd last digit) is answered with the ordinary response — {mti('0421')} with {mti('0430')}. A response is never answered.
        </p>
      </Card>

      <Card title={`Response codes the switch sends (DE ${rc.field})`}>
        <Table head={['Code', 'Meaning', 'When', 'What to do']} mono={[0]} rows={responseRows} />
        <p className="text-xs text-gray-500 mt-3">
          {d.reversal ? (
            <>A reversal names its original in {originalElement(d.originalData)}. In one the switch sends ({mti('0420')}), DE <span className="font-mono">{d.reversal.reasonField}</span> carries the reason
              {d.reversal.timeoutReason && <>: <span className="font-mono">{d.reversal.timeoutReason}</span> for a time-out</>}.</>
          ) : (
            <>This profile states no reversal convention, so the switch does not originate reversals on it.</>
          )}
        </p>
      </Card>

      <Card title={`Network management (DE ${nm.field} in ${nm.requestMti} / ${nm.responseMti})`}>
        <Table head={['Code', 'Function', 'The switch']} mono={[0]} rows={nmRows} />
      </Card>

      <Card title="Data elements used on this switch">
        {elements ? (
          <Table head={['DE', 'Name', 'Format', 'Notes']} mono={[0, 2]} rows={elements} />
        ) : (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-2">
            <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
            <p className="text-sm text-blue-900">
              This deployment does not publish its element table. Ask AfricaNenda for the specification you are
              integrating against; <Link to="/iso8583/explain" className="underline">Explain a Frame</Link> still reads
              any frame against it.
            </p>
          </div>
        )}
        <p className="text-xs text-gray-500 mt-3">
          The whole table, with every element the profile defines, is on{' '}
          <Link to="/iso8583/dialect" className="underline">Dialect</Link>.
        </p>
      </Card>
    </div>
  );
}
